import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Truck, MapPin, Droplets, Clock, Printer, ExternalLink } from "lucide-react";

import { API_BASE } from "@/lib/api";
import { unitLabel } from "@/lib/industry";
const API = API_BASE;

const STAGES = [
  { key: "loaded", label: "Loaded", ts: "loaded_at" },
  { key: "en_route", label: "En route", ts: "departed_at" },
  { key: "on_site", label: "On site", ts: "arrived_at" },
  { key: "delivered", label: "Delivered", ts: "completed_at" },
];

function stageIndex(status) {
  const i = STAGES.findIndex((s) => s.key === status);
  return i < 0 ? 0 : i;
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const num = (n, u = "") => (n == null ? "—" : `${n}${u ? ` ${u}` : ""}`);

export default function PublicDocket() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/pub/docket/${token}`);
      setData(d);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Docket not found");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="public-docket-error">
        <div className="border border-red-600 bg-red-950/30 px-6 py-4 mono uppercase tracking-widest text-sm">{err}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center mono uppercase tracking-widest text-xs">Loading…</div>;
  }

  const stage = stageIndex(data.status);
  const isDelivered = data.status === "delivered";
  const isCancelled = data.status === "cancelled";
  const totalWater = Number(data.slump_water_l || 0) + Number(data.site_water_adjust_l || 0);
  const qty = Math.max(1, Number(data.quantity_m3 || 1));
  const perM3 = totalWater / qty;
  const outsideAS = perM3 > 20 || totalWater > 20;
  const subtotal = Number(data.quantity_m3 || 0) * Number(data.price_per_m3 || 0);
  const pdfUrl = `${API}/pub/docket/${token}/pdf`;

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-docket-view">
      <div className="max-w-lg mx-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500">READY-MIX CONCRETE</div>
            <div className="heading text-3xl font-black uppercase" data-testid="public-docket-supplier">{data.supplier?.brand || "ARMORBATCH"} <span className="text-primary-orange">{data.supplier?.tagline || "GLOBAL"}</span></div>
            {data.plant_name && (
              <div className="mono text-[10px] text-neutral-500 mt-1">{data.plant_name}{data.plant_address ? ` · ${data.plant_address}` : ""}</div>
            )}
          </div>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="pub-docket-pdf-link"
            className="mono text-[10px] uppercase tracking-widest border border-primary-orange text-primary-orange px-2 py-1.5 hover:bg-primary-orange hover:text-black inline-flex items-center gap-1 shrink-0"
          >
            <Printer className="w-3 h-3" /> PDF <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {isCancelled && (
          <div className="border border-red-500 bg-red-950/40 px-3 py-2 mono text-[11px] uppercase tracking-widest text-red-300" data-testid="pub-cancelled-banner">
            ⚠ This docket was cancelled
          </div>
        )}

        <div className={`border p-4 ${isCancelled ? "border-red-500/60 bg-red-950/10" : "border-primary-orange bg-orange-950/10"}`}>
          <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">Delivery Docket</div>
          <div className="heading text-2xl font-black mono" data-testid="pub-docket-no">{data.docket_no}</div>
          <div className="text-sm text-neutral-300 mt-1">{data.customer_name}</div>
          {data.customer_abn && <div className="mono text-[10px] text-neutral-500">ABN {data.customer_abn}</div>}
          <div className="text-xs text-neutral-400 flex items-start gap-1 mt-1"><MapPin className="w-3 h-3 mt-0.5" />{data.site_address}</div>
          {data.po_number && <div className="mono text-[10px] text-neutral-500 mt-1">PO {data.po_number}</div>}
        </div>

        {!isCancelled && (
          <div className="border border-neutral-800 p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Progress</div>
            <div className="grid grid-cols-4 gap-1 mb-3" data-testid="pub-progress-bar">
              {STAGES.map((s, i) => (
                <div key={s.key} className={`h-1.5 ${i <= stage ? "bg-primary-orange" : "bg-neutral-800"}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {STAGES.map((s, i) => (
                <div key={s.key} className={`flex items-center gap-2 ${i <= stage ? "text-white" : "text-neutral-600"}`}>
                  {i <= stage ? <CheckCircle2 className="w-4 h-4 text-primary-orange" /> : <Clock className="w-4 h-4" />}
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest">{s.label}</div>
                    <div className="text-xs">{fmt(data[s.ts])}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="border border-neutral-800 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500">Mix</div>
            <div className="mono text-lg font-bold">{data.mix_code}</div>
            {data.mix?.strength_mpa && <div className="mono text-[10px] text-neutral-500">{data.mix.strength_mpa} MPa</div>}
          </div>
          <div className="border border-neutral-800 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500">Quantity</div>
            <div className="mono text-lg font-bold">{data.quantity_m3} <span className="text-xs text-neutral-500">{unitLabel(data.unit)}</span></div>
            {data.slump_mm && <div className="mono text-[10px] text-neutral-500">{data.slump_mm}mm slump</div>}
          </div>
          <div className="border border-neutral-800 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><Truck className="w-3 h-3" />Truck</div>
            <div className="mono text-lg font-bold">{data.truck_number || "—"}</div>
            <div className="mono text-[10px] text-neutral-500 truncate">{data.driver_name}</div>
          </div>
        </div>

        {/* ─── Extras / Additives ─── */}
        {data.extras && data.extras.length > 0 && (
          <div className="border border-neutral-800 p-4" data-testid="pub-extras">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Additives / Extras</div>
            <div className="flex flex-wrap gap-2">
              {data.extras.map((x, i) => (
                <span
                  key={i}
                  className="mono text-[10px] uppercase tracking-widest px-2 py-1 font-bold"
                  style={{ background: x.color || "#404040", color: "#fff" }}
                >
                  {x.name}{x.price_per_m3 > 0 ? ` · +$${x.price_per_m3.toFixed(2)}/m³` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ─── Pricing ─── */}
        {!isCancelled && (data.total > 0 || subtotal > 0) && (
          <div className="border border-neutral-800 p-4" data-testid="pub-pricing">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Charges</div>
            <div className="space-y-1 mono text-xs">
              <div className="flex justify-between">
                <span className="text-neutral-400">{data.unit && data.unit !== "m3" ? (data.mix_code || "Delivery") : "Concrete"} · {data.quantity_m3}{unitLabel(data.unit)} @ ${Number(data.price_per_m3 || 0).toFixed(2)}/{unitLabel(data.unit)}</span>
                <span>{money(subtotal)}</span>
              </div>
              {Number(data.extras_charge) > 0 && (
                <div className="flex justify-between">
                  <span className="text-neutral-400">Extras / Additives</span>
                  <span>{money(data.extras_charge)}</span>
                </div>
              )}
              {Number(data.small_load_charge) > 0 && (
                <div className="flex justify-between">
                  <span className="text-neutral-400">Small-load cartage shortfall</span>
                  <span>{money(data.small_load_charge)}</span>
                </div>
              )}
              {Number(data.wait_charge) > 0 && (
                <div className="flex justify-between">
                  <span className="text-neutral-400">Waiting time · {data.wait_minutes_chargeable} min</span>
                  <span>{money(data.wait_charge)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-neutral-700 pt-2 mt-2 text-neutral-300">
                <span>Subtotal (ex GST)</span>
                <span>{money(data.total)}</span>
              </div>
              <div className="flex justify-between text-neutral-300">
                <span>GST (10%)</span>
                <span>{money(Number(data.total || 0) * 0.1)}</span>
              </div>
              <div className="flex justify-between border-t border-neutral-700 pt-2 mt-1 text-primary-orange font-bold text-sm" data-testid="pub-total-inc-gst">
                <span>Total (inc GST)</span>
                <span>{money(Number(data.total || 0) * 1.1)}</span>
              </div>
            </div>
          </div>
        )}

        {(totalWater > 0 || data.approx_slump_mm) && (
          <div
            data-testid="pub-water-panel"
            className={`border p-4 space-y-1 ${outsideAS ? "border-red-500 bg-red-950/40" : "border-primary-orange/50 bg-orange-950/10"}`}
          >
            <div className={`mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ${outsideAS ? "text-red-300" : "text-primary-orange"}`}>
              <Droplets className="w-3 h-3" />Water added on site
            </div>
            <div className="mono text-xs">
              Slump water: <b>{data.slump_water_l || 0} L</b> · Site adjust: <b>{data.site_water_adjust_l || 0} L</b> · Total: <b>{totalWater} L ({perM3.toFixed(1)} L/m³)</b>
            </div>
            {data.approx_slump_mm ? (
              <div className="mono text-[11px] text-neutral-400">Measured slump after water: {data.approx_slump_mm} mm</div>
            ) : null}
            {outsideAS && (
              <div className="text-[11px] text-red-300 leading-snug pt-2 border-t border-red-500/40 mt-2">
                {"⚠ Water addition exceeds AS 1379 (>20 L/m³). The site signatory accepted responsibility for the additional water; the supplier takes NO responsibility for the strength or durability of concrete compromised by additional water beyond Australian Standards."}
              </div>
            )}
          </div>
        )}

        {data.notes && (
          <div className="border border-neutral-800 p-3" data-testid="pub-notes">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Notes</div>
            <div className="text-xs text-neutral-300 leading-relaxed">{data.notes}</div>
          </div>
        )}

        <div className={`border p-4 ${isDelivered ? "border-emerald-500/60 bg-emerald-950/20" : "border-neutral-800"}`}>
          <div className="mono text-[10px] uppercase tracking-widest text-neutral-500">Signature</div>
          {isDelivered ? (
            <>
              <div className="text-sm">Signed by <b>{data.signed_by || "site representative"}</b></div>
              <div className="mono text-[11px] text-neutral-400">Delivered {fmt(data.completed_at)}</div>
              {data.signature_url && (
                <div className="mt-2 bg-white p-2 inline-block" data-testid="pub-signature-img">
                  <img src={data.signature_url} alt="Site signature" className="max-h-24" />
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-neutral-400">Awaiting on-site signature. This page refreshes automatically.</div>
          )}
        </div>

        {data.photo_url && (
          <div className="border border-neutral-800 p-3" data-testid="pub-photo">
            <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Delivery Photo</div>
            <img src={data.photo_url} alt="Delivery" className="w-full border border-neutral-800" />
          </div>
        )}

        <div className="mono text-[10px] uppercase tracking-widest text-neutral-600 text-center pt-4">
          Live tracking · Refreshes every 15s
        </div>
      </div>
    </div>
  );
}
